import React, { useEffect, useRef, useState } from 'react';
import { Check, Clock3, ChevronRight, ScanLine, ShieldCheck, X } from 'lucide-react';
import { recordLaboratoryStage } from './api';
import { labDay, labError, labWhen } from './LaboratoryParts';
import { applyStageScan, readingsAverage, stageScanTarget, useLabScan } from './labScanContext';
import { fieldLabel, itemLabel, unitSymbol } from './LaboratoryText';
import { Pill, XomicsSelect } from './parts';
import { tx } from './i18n';
import './laboratory-workflows.css';
import './laboratory-scan.css';

const text = (key, values) => tx(`ui.lab.stage.${key}`, values);
const today = () => new Date().toISOString().slice(0, 10);

// A recorded value as the lab reads it: the option label, the number with its
// unit, the day, or the text.
export function RecordValue({ value }) {
  if (value.valueType === 'option') return <>{itemLabel(value.value)}</>;
  if (value.valueType === 'options') return <>{value.value.map(item => item.catalogueCode ? `${item.catalogueCode} ${itemLabel(item)}` : itemLabel(item)).join(', ')}</>;
  if (value.valueType === 'number') return <>{value.value.toLocaleString()}{value.unit ? ` ${value.unit}` : ''}</>;
  if (value.valueType === 'boolean') return <>{tx(value.value ? 'ui.lab.yes' : 'ui.lab.no')}</>;
  if (value.valueType === 'date') return <>{labDay(value.value)}</>;
  return <>{value.value}</>;
}

export function RecordHistory({ attempts, open = false }) {
  if (attempts.length === 0) return null;
  return <details className="xo-stage-history" open={open}>
    <summary><Clock3 aria-hidden="true" />{text('history', { count: attempts.length })}<ChevronRight aria-hidden="true" /></summary>
    <ol>{attempts.map(attempt => <li key={attempt.uuid}>
      <div className="xo-stage-history-head"><strong>{text('attempt', { number: attempt.attemptNo })}</strong>
        <Pill tone={attempt.outcome === 'pass' ? 'ok' : 'warn'}>{text(`outcome.${attempt.outcome}`)}</Pill>
        {attempt.scanned && <span className="xo-scan-mark"><ScanLine aria-hidden="true" />{tx('ui.lab.scan.scanned')}</span>}
        {attempt.status !== 'completed' && <Pill tone={attempt.status === 'rejected' ? 'bad' : 'warn'}>{text(`status.${attempt.status}`)}</Pill>}
        {attempt.nextAction && attempt.nextAction.effect !== 'advance' && <span>{tx(`ui.lab.next.${attempt.nextAction.code}`)}</span>}</div>
      <p>{text('performed', { name: attempt.performedBy, at: labWhen(attempt.performedAt) })}</p>
      {attempt.values.length > 0 && <dl className="xo-stage-values">{attempt.values.map(value => <div key={value.fieldId}><dt>{fieldLabel(value.code)}</dt><dd><RecordValue value={value} /></dd></div>)}</dl>}
      {attempt.devices.length > 0 && <p className="xo-stage-devices-used">{attempt.devices.map(device => `${device.name} (${device.inventoryNumber})`).join(', ')}</p>}
      {attempt.note && <blockquote>{attempt.note}</blockquote>}
      {attempt.reviewedAt !== null && <p>{text('reviewed', { name: attempt.reviewedBy, at: labWhen(attempt.reviewedAt) })}</p>}
      {attempt.reviewNote && <blockquote>{attempt.reviewNote}</blockquote>}
    </li>)}</ol>
  </details>;
}

// The choices a field offers: the active ones, and for a field that depends on
// another only those of the choice made there.
export function fieldItems(field, values) {
  const parent = field.parentFieldId === null ? null : values[field.parentFieldId];
  return field.list.items.filter(item => item.isActive && (field.parentFieldId === null || item.parentItemId === null
    || (parent && item.parentItemId === parent.id)));
}

export function FieldInput({ field, value, values, onChange, disabled, idPrefix = 'stage-field', flash = false, hint = null, bare = false }) {
  const id = `${idPrefix}-${field.id}`;
  // On its own in a table cell the control carries the field's name itself.
  const named = bare ? { 'aria-label': fieldLabel(field.code) } : {};
  const grouped = bare ? named : { 'aria-labelledby': `${id}-label` };
  let control;
  if (field.valueType === 'option' || field.valueType === 'options') {
    const parent = field.parentFieldId === null ? null : values[field.parentFieldId];
    const items = fieldItems(field, values);
    if (field.role === 'outcome') {
      control = <div className="xo-stage-outcome" role="radiogroup" {...grouped}>{items.map(item => <button type="button" role="radio" key={item.code}
        aria-checked={value && value.code === item.code} className={value && value.code === item.code ? `is-active is-${item.outcome}` : ''} disabled={disabled}
        onClick={() => onChange(item)}>{item.outcome === 'pass' ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}{itemLabel(item)}</button>)}</div>;
    } else if (field.valueType === 'options') {
      const chosen = value || [];
      control = <div className="xo-stage-options" role="group" {...grouped}>{items.map(item => <label key={item.code} className={chosen.some(c => c.code === item.code) ? 'is-active' : ''}>
        <input type="checkbox" checked={chosen.some(c => c.code === item.code)} disabled={disabled}
          onChange={() => onChange(chosen.some(c => c.code === item.code) ? chosen.filter(c => c.code !== item.code) : [...chosen, item])} />
        <span>{item.catalogueCode && <code>{item.catalogueCode}</code>}{itemLabel(item)}</span></label>)}</div>;
    } else {
      control = <XomicsSelect id={id} {...named} value={value ? value.code : ''} disabled={disabled || (field.parentFieldId !== null && !parent)}
        onChange={event => onChange(items.find(item => item.code === event.target.value) || null)}>
        <option value="">{field.parentFieldId !== null && !parent ? text('choose_parent_first') : text('choose')}</option>
        {items.map(item => <option key={item.code} value={item.code}>{itemLabel(item)}</option>)}
      </XomicsSelect>;
    }
  } else if (field.valueType === 'number') {
    control = <span className="xo-stage-number"><input id={id} {...named} type="number" step="any" inputMode="decimal" value={value ?? ''} disabled={disabled}
      min={field.unit && field.unit.allowsNegative ? undefined : 0} onChange={event => onChange(event.target.value)} />{field.unit && <b>{unitSymbol(field.unit)}</b>}</span>;
  } else if (field.valueType === 'date') {
    control = <input id={id} {...named} type="date" value={value ?? ''} max={today()} disabled={disabled} onChange={event => onChange(event.target.value)} />;
  } else if (field.valueType === 'boolean') {
    control = <div className="xo-stage-outcome" role="radiogroup" {...grouped}>{[true, false].map(option => <button type="button" role="radio" key={String(option)}
      aria-checked={value === option} className={value === option ? 'is-active' : ''} disabled={disabled} onClick={() => onChange(option)}>{tx(option ? 'ui.lab.yes' : 'ui.lab.no')}</button>)}</div>;
  } else {
    control = <input id={id} {...named} value={value ?? ''} maxLength={2000} disabled={disabled} onChange={event => onChange(event.target.value)} />;
  }
  if (bare) return control;
  return <div className={`xo-stage-field is-${field.valueType}${field.role === 'outcome' ? ' is-outcome' : ''}${flash ? ' xo-scan-flash' : ''}`}>
    <label id={`${id}-label`} htmlFor={id}>{fieldLabel(field.code)}{field.isRequired && <span className="xo-stage-required" aria-label={text('required')}>*</span>}</label>
    {control}
    {hint}
  </div>;
}

export function payload(stage, values) {
  const out = {};
  for (const field of stage.fields) {
    const value = values[field.id];
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) continue;
    out[field.code] = field.valueType === 'option' ? value.code : field.valueType === 'options' ? value.map(item => item.code)
      : field.valueType === 'number' ? Number(value) : typeof value === 'string' ? value.trim() : value;
  }
  return out;
}

// The form of the current stage, built from its field definitions.
export default function LaboratoryStageForm({ order, stage, onRecorded }) {
  const [values, setValues] = useState({}), [devices, setDevices] = useState([]), [nextAction, setNextAction] = useState('');
  const [note, setNote] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [scanned, setScanned] = useState(false), [flash, setFlash] = useState(null);
  const retry = useRef(null), inFlight = useRef(false), root = useRef(null);
  // Averages this form worked out itself; one typed over by hand is left alone.
  const averaged = useRef(new Set());
  const latest = useRef({ values, devices });
  latest.current = { values, devices };
  const set = (field, value) => setValues((current) => {
    const next = { ...current, [field.id]: value };
    for (const child of stage.fields.filter(f => f.parentFieldId === field.id)) delete next[child.id];
    averaged.current.delete(field.id);
    const average = readingsAverage(stage.fields, next, field);
    if (average !== null && (averaged.current.has(average.field.id) || next[average.field.id] === undefined || next[average.field.id] === '')) {
      next[average.field.id] = average.value;
      if (average.value === '') averaged.current.delete(average.field.id); else averaged.current.add(average.field.id);
    }
    return next;
  });
  const dirty = Object.keys(values).length > 0 || devices.length > 0 || note.trim() !== '';
  // A scan while this form is on screen: its own tube confirms it; one of the
  // stage's machines or choices is filled in. Anything else is not this form's.
  const { station, touch } = useLabScan({
    tubeCode: order.tube.code,
    // The tube as printed on its label: a product kit's carries the prefix (GH453369-01).
    tubeLabel: order.tube.printedCode ?? order.tube.code,
    dirty,
    onTube: () => { setScanned(true); setFlash('tube'); touch(); if (root.current) root.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); },
    match: (code) => {
      const target = stageScanTarget(stage, code);
      if (target === null) return null;
      return () => {
        const applied = applyStageScan(stage, target, latest.current.values, latest.current.devices);
        const title = target.kind === 'device' ? target.device.name : itemLabel(target.item);
        if (!applied.ok) {
          return { tone: 'bad', title, detail: applied.reason === 'method_first' ? text('choose_method_first') : tx(`ui.lab.device.status.${target.device.status}`) };
        }
        setValues(applied.values); setDevices(applied.devices);
        setFlash(target.kind === 'device' ? 'device' : target.field.id);
        return { tone: 'ok', title, detail: target.kind === 'device' ? target.device.inventoryNumber : fieldLabel(target.field.code) };
      };
    },
  });
  // A tube scanned on another page, on its way here, arrives confirmed.
  useEffect(() => { if (station && station.wasScanned(order.tube.code)) setScanned(true); }, [station, order.tube.code]);
  useEffect(() => {
    if (flash === null) return undefined;
    const timer = window.setTimeout(() => setFlash(null), 900);
    return () => window.clearTimeout(timer);
  }, [flash]);
  const outcomeField = stage.fields.find(field => field.role === 'outcome');
  const outcome = outcomeField ? values[outcomeField.id] ? values[outcomeField.id].outcome : null : 'pass';
  const methodField = stage.fields.find(field => field.role === 'method');
  const method = methodField ? values[methodField.id] : null;
  const allowedDevices = stage.deviceRule === 'per_method' ? stage.devices.filter(device => method && method.deviceIds.includes(device.id)) : stage.devices;
  const followUps = outcome && outcome !== 'pass' ? stage.outcomeActions.filter(action => action.outcome === outcome) : [];
  const toggleDevice = id => setDevices(current => stage.deviceRule === 'one_or_more'
    ? current.includes(id) ? current.filter(value => value !== id) : [...current, id] : [id]);
  async function submit() {
    if (inFlight.current) return;
    const body = { workOrderUuid: order.uuid, expectedStageId: stage.id, values: payload(stage, values),
      deviceIds: devices.filter(id => allowedDevices.some(device => device.id === id)),
      ...(followUps.length && nextAction ? { nextActionCode: nextAction } : {}), ...(note.trim() ? { note } : {}),
      // A confirmed tube goes on the record with the scanner that read it.
      ...(scanned && station && station.scanner !== '' ? { scan: { sampleCode: order.tube.code, deviceId: station.scanner } } : {}) };
    const signature = JSON.stringify(body);
    if (retry.current === null || retry.current.signature !== signature) retry.current = { signature, requestId: crypto.randomUUID() };
    inFlight.current = true; setBusy(true); setError('');
    try {
      const result = await recordLaboratoryStage({ ...body, requestId: retry.current.requestId });
      retry.current = null; onRecorded(result);
    } catch (caught) { setError(labError(caught)); }
    finally { inFlight.current = false; setBusy(false); }
  }
  return <div className="xo-stage-form" ref={root} onFocusCapture={touch} onPointerDownCapture={touch}>
    {station && <p className={`xo-scan-tube${scanned ? ' is-confirmed' : ''}${flash === 'tube' ? ' xo-scan-flash' : ''}`}>
      {scanned ? <Check aria-hidden="true" /> : <ScanLine aria-hidden="true" />}
      <span>{tx(scanned ? 'ui.lab.scan.tube_confirmed' : 'ui.lab.scan.tube_to_confirm')} <code>{order.tube.printedCode ?? order.tube.code}</code></span></p>}
    <div className="xo-stage-fields">{[...stage.fields].sort((a, b) => a.position - b.position).map(field => <FieldInput key={field.id} field={field}
      value={values[field.id]} values={values} disabled={busy} flash={flash === field.id}
      hint={averaged.current.has(field.id) && values[field.id] !== '' ? <small className="xo-stage-calculated">{tx('ui.lab.scan.average')}</small> : null}
      onChange={value => set(field, value)} />)}</div>
    {stage.deviceRule !== 'none' && <fieldset className={`xo-stage-devices${flash === 'device' ? ' xo-scan-flash' : ''}`}>
      <legend>{text(`device_rule.${stage.deviceRule}`)}</legend>
      {stage.deviceRule === 'per_method' && !method ? <p>{text('choose_method_first')}</p>
        : allowedDevices.length === 0 ? <p>{text('method_without_device')}</p>
          : allowedDevices.map(device => <label key={device.id} className={`${devices.includes(device.id) ? 'is-active' : ''}${device.status !== 'in_service' ? ' is-disabled' : ''}`}>
            <input type={stage.deviceRule === 'one_or_more' ? 'checkbox' : 'radio'} name={`devices-${stage.id}`} checked={devices.includes(device.id)}
              disabled={busy || device.status !== 'in_service'} onChange={() => toggleDevice(device.id)} />
            <span><strong>{device.name}</strong><small>{device.inventoryNumber}</small></span>
            {device.status !== 'in_service' && <Pill tone="warn">{tx(`ui.lab.device.status.${device.status}`)}</Pill>}
          </label>)}
    </fieldset>}
    {followUps.length > 0 && <fieldset className="xo-stage-next">
      <legend>{text('next_action')}</legend>
      {followUps.map(action => <label key={action.nextAction.code} className={nextAction === action.nextAction.code ? 'is-active' : ''}>
        <input type="radio" name={`next-${stage.id}`} checked={nextAction === action.nextAction.code} disabled={busy} onChange={() => setNextAction(action.nextAction.code)} />
        <span><strong>{tx(`ui.lab.next.${action.nextAction.code}`)}</strong></span></label>)}
    </fieldset>}
    {outcome && outcome !== 'pass' && followUps.length === 0 && <p className="xo-stage-hint">{text('no_next_step')}</p>}
    <label className="xo-stage-note"><span>{text('note')}</span><textarea rows={2} maxLength={2000} value={note} disabled={busy} onChange={event => setNote(event.target.value)} /></label>
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
    <div className="xo-stage-actions"><button type="button" className="xo-btn" onClick={submit} disabled={busy}>
      {stage.approvalRequirement === 'required' ? <ShieldCheck aria-hidden="true" /> : <Check aria-hidden="true" />}
      {text(busy ? 'saving' : stage.approvalRequirement === 'required' ? 'submit_approval' : 'record')}</button></div>
  </div>;
}
